import React from 'react';
import { I18n } from '@aws-amplify/core';
import { Col, Form, Radio, RadioChangeEvent, Row } from 'antd';

import * as S from './styles';
import { RadioGroup } from '../../styles';
import { FormItem } from '../../../styles';
import { Text } from '@/components/Typography';
import { useCategoryContext } from '../context';
import { ROSA_ELEMENTS_ICONS } from './constants';
import { useRosaContext } from '../../../../../context';
import { SubOptionalElement } from './SubOptionalElement';
import { Element } from '../../../../../hooks/useGetElements';
import { useUpdateElement } from '../../../../../hooks/useUpdateElement';
import { RosaElementEnum, RosaSubCategoryEnum } from '../../../constants';

const { useWatch, useFormInstance } = Form;

interface RequiredElementProps {
	requiredMapper: Record<string, Element>;
}

export function RequiredElement({ requiredMapper }: Readonly<RequiredElementProps>) {
	const form = useFormInstance();

	const { organization_id, company_id, file_id } = useRosaContext();
	const { fieldName, selectedSubCategory, elements } = useCategoryContext();
	const { mutateAsync: updateElement } = useUpdateElement({ organization_id, company_id, report_id: file_id });

	const requiredItemId = useWatch([...fieldName, 'required']);

	async function handleUpdateRequiredElement(e: RadioChangeEvent): Promise<void> {
		const elementId = e.target.value;

		const elementName = requiredMapper[elementId].name;

		if (elementName !== RosaElementEnum.VERY_LOW && selectedSubCategory.name === RosaSubCategoryEnum.MONITOR) {
			await deselectSubElement();
		}

		const payloads = elements?.required.map((element) => {
			const checked = element.id === elementId;

			return {
				evaluation_element: {
					checked,
					id: element?.id
				}
			};
		});

		const sortedPayloads = sortRadioFields(payloads);

		for (const payload of sortedPayloads) {
			await updateElement(payload);
		}
	}

	async function deselectSubElement(): Promise<void> {
		const selectedElement = elements?.not_required.find((element) => element.name === RosaElementEnum.FAR_AWAY);

		form.setFieldValue([...fieldName, 'not_required', RosaElementEnum.FAR_AWAY], false);

		await updateElement({
			evaluation_element: {
				id: selectedElement?.id,
				checked: false
			}
		});
	}

	function sortRadioFields(payloads: any): any {
		return payloads.sort((a: any, b: any) => {
			if (a.evaluation_element.checked === b.evaluation_element.checked) {
				return 0;
			}

			if (a.evaluation_element.checked) {
				return 1;
			}

			return -1;
		});
	}

	return (
		<FormItem name={[...fieldName, 'required']} required>
			<RadioGroup onChange={handleUpdateRequiredElement}>
				<Row gutter={[20, 0]} justify="center">
					{elements?.required.map((item: any, _: number, array: any) => {
						const totalItems = array.length;
						const isSelected = item.id === requiredItemId;

						return (
							<Col key={item.id} span={24 / totalItems}>
								<Radio.Button value={item.id}>
									<Row justify="center" gutter={[0, 20]}>
										<S.CardScore span={24} $isSelected={isSelected}>
											<Text>{item.score}</Text>
										</S.CardScore>
										<Col span={24}>{ROSA_ELEMENTS_ICONS[item.name]}</Col>
										<Col span={24}>
											<span>{I18n.get(item.description)}</span>
										</Col>
										{item.name === RosaElementEnum.VERY_LOW && (
											<Col span={24}>
												<SubOptionalElement
													disabled={!isSelected}
													name={elements?.not_required[0].name}
													score={elements?.not_required[0].score}
												>
													{I18n.get(elements?.not_required[0].description)}
												</SubOptionalElement>
											</Col>
										)}
									</Row>
								</Radio.Button>
							</Col>
						);
					})}
				</Row>
			</RadioGroup>
		</FormItem>
	);
}
